// The fridge: countdown timers for things that go off, ported from perishable.
//
// Registered as a NAMESPACED module rather than folded into the main store,
// for two reasons. The fridge is reached by a different door — the wall tablet
// arrives with a capability key and no Google session — so its state has to be
// able to exist when `userEmail` is null and no hat is loaded. And every other
// hat in the database is fridgeless; keeping this self-contained is what makes
// the feature opt-in rather than a schema change landing in 13 accounts.

import { ref, push, set, update, remove, get, onValue, off, query, limitToLast, serverTimestamp } from 'firebase/database'
import { db, ensureSession } from '@/firebase'
import { sortTimers, spanInDays, formatDaySpan } from './timers'
import { foldObservation } from './shelfLife'
import { sortHistory, HISTORY_LIMIT } from './history'
import { timersPath, templatesPath, historyPath, templateKey } from './paths'
import { reconcileShelfLives, fridgeFoodId } from './reconcile'
import { pairingPath, isFreshPairing } from '@/utils/fridge/pairing'
import { isValidKey } from '@/utils/fridge/fridgeKey'

// Every real fridge has templates — the migration wrote them and every
// signed-in visit republishes them — so their absence is the honest test for
// "no fridge has this key". Timers are checked too, in case a fridge was ever
// emptied of templates by hand. A read failure counts as absent: either way
// this key is not one to subscribe to.
const fridgeExists = async (key) => {
  try {
    const templates = await get(ref(db, templatesPath(key)))
    if (templates.exists()) return true
    const timers = await get(ref(db, timersPath(key)))
    return timers.exists()
  } catch {
    return false
  }
}

export default {
  namespaced: true,

  state: () => ({
    fridgeKey: null,
    timers: {},
    templates: {},
    history: {},
    loading: false,
    subscribedTo: null,

    // 'unauthorized' means the database REFUSED us (bad or revoked key). The
    // wall display turns that into a loud full-screen state, because on a
    // screen nobody interacts with, a quiet failure is an invisible one.
    error: null
  }),

  getters: {
    allTimers: (state) => sortTimers(state.timers),

    // What the SCREENS show — the wall and the phone's on-hand strip.
    //
    // Matt's call, 2026-09-20: "we should try to only list things that are
    // perishable. We don't need to list that I have a can of beans that's
    // gonna last for years." A talk-through picks up the whole kitchen,
    // cupboards included, and all of it is worth knowing for the shopping
    // list — but a wall display glanced at from across the room stops working
    // the moment it becomes ninety rows of flour and tinned tomatoes.
    //
    // So pantry stores are TRACKED and not DISPLAYED. Everything that decides
    // what to buy — `onHandUntil`, the coverage math, cooking a meal — keeps
    // reading `allTimers` and sees the whole house.
    displayTimers: (state) => sortTimers(state.timers).filter((timer) => !timer.shelfStable),
    timerById: (state) => (id) => state.timers[id],
    templates: (state) => Object.values(state.templates),
    history: (state) => sortHistory(state.history),
    hasFridge: (state) => Boolean(state.fridgeKey),

    // Shelf life by normalized food name, for the staple logic on the shopping
    // list. This is the whole point of the merge: `lastPurchased` plus a
    // 60-day default is a GUESS at whether the olive oil is still there, and
    // the fridge holds the answer.
    // Normalized food name -> the LATEST expiry among its live timers.
    //
    // This is what lets the shopping list stop guessing. It answers exactly one
    // question — "is this food physically in the house right now?" — and the
    // staple logic may only ever act on a YES. A name missing from here means
    // nothing was photographed, not that the cupboard is bare.
    //
    // Latest rather than soonest: two cartons of eggs means you have eggs until
    // the later one goes. Taking the soonest would put eggs back on the list
    // while a good carton sat in the door.
    onHandUntil: (state) => {
      const out = {};
      Object.values(state.timers || {}).forEach((timer) => {
        const name = String(timer?.title || '').trim().toLowerCase();
        if (!name || !timer.expiryDate) return;
        const at = new Date(timer.expiryDate).getTime();
        if (Number.isNaN(at)) return;
        if (!out[name] || at > new Date(out[name]).getTime()) out[name] = timer.expiryDate;
      });
      return out;
    },

    shelfLifeByName: (state) => {
      const out = {}
      Object.values(state.templates || {}).forEach((template) => {
        if (!template?.title || !Number.isInteger(template.days)) return
        out[String(template.title).trim().toLowerCase()] = template.days
      })
      return out
    }
  },

  mutations: {
    SET_FRIDGE_KEY (state, key) { state.fridgeKey = key || null },
    SET_LOADING (state, loading) { state.loading = loading },
    SET_ERROR (state, error) { state.error = error },
    SET_TIMERS (state, timers) { state.timers = timers || {} },
    SET_TEMPLATES (state, templates) { state.templates = templates || {} },
    SET_HISTORY (state, history) { state.history = history || {} },
    SET_SUBSCRIBED_TO (state, key) { state.subscribedTo = key || null },
    CLEAR (state) {
      state.timers = {}
      state.templates = {}
      state.history = {}
      state.error = null
      state.subscribedTo = null
    }
  },

  actions: {
    // Attach the listeners for one fridge.
    //
    // Guarded on `subscribedTo` rather than on the data being empty. An empty
    // fridge is a perfectly normal answer — it is what a brand new one looks
    // like — so an emptiness check would re-attach a fresh listener every time
    // the route was entered.
    //
    // Takes a key, or `{ key, verify }`. `verify` is for a key that arrived
    // by URL or was typed: the rules grant any well-formed key a read of its
    // own (empty) node, so a key with one character wrong subscribes fine and
    // shows an EMPTY FRIDGE — which is what Matt's tablet did on 2026-09-11
    // after the key was typed in by hand. A verified key that names no fridge
    // is reported as `unknown` instead, and nothing is subscribed. The hat's
    // own pointer is never verified: it came from the database, not a keyboard.
    async subscribe ({ commit, state }, payload) {
      const { key: given, verify = false } = typeof payload === 'string' || !payload
        ? { key: payload }
        : payload
      const key = given || state.fridgeKey
      if (!key) {
        commit('SET_ERROR', 'unauthorized')
        return
      }
      if (state.subscribedTo === key) return

      // Loading goes up BEFORE the first await. The wall renders its grid the
      // moment loading is false and the timer list is empty, so a session
      // handshake with loading still false paints an empty wall with a lone
      // Add Timer card for a second on every load.
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      // The token has to exist before the first read, or the SDK sends the
      // request unauthenticated and the rules answer with a permission error
      // that reads exactly like a bad key.
      await ensureSession()

      if (verify && !(await fridgeExists(key))) {
        commit('SET_ERROR', 'unknown')
        commit('SET_LOADING', false)
        return
      }

      commit('SET_FRIDGE_KEY', key)
      commit('SET_SUBSCRIBED_TO', key)

      onValue(ref(db, timersPath(key)), (snapshot) => {
        commit('SET_TIMERS', snapshot.val())
        commit('SET_ERROR', null)
        commit('SET_LOADING', false)
      }, (error) => {
        console.error('Failed to load fridge timers:', error)
        commit('SET_ERROR', 'unauthorized')
        commit('SET_LOADING', false)
      })

      onValue(ref(db, templatesPath(key)), (snapshot) => {
        commit('SET_TEMPLATES', snapshot.val())
      }, (error) => {
        console.error('Failed to load fridge templates:', error)
      })

      // Bounded, and its failure is swallowed on purpose: the timers are the
      // app, the change log is a convenience.
      onValue(query(ref(db, historyPath(key)), limitToLast(HISTORY_LIMIT)), (snapshot) => {
        commit('SET_HISTORY', snapshot.val())
      }, (error) => {
        console.error('Failed to load fridge history:', error)
      })
    },

    // The phone's half of pairing a wall display: publish this hat's key under
    // the code the wall is showing. Only a signed-in member has the pointer,
    // and the rules refuse the write from an anonymous session anyway.
    async offerPairing ({ rootState }, code) {
      const key = rootState.fridgeKeyForHat
      if (!key) throw new Error('This account has no fridge to pair.')
      await ensureSession()
      await set(ref(db, pairingPath(code)), { key, createdAt: serverTimestamp() })
    },

    // The wall's half: wait at `pairings/<code>` for a phone to answer.
    // Resolves with the key, having deleted the pairing so the code is spent
    // the instant it is used. `cancel` detaches the listener — the screen
    // calls it on unmount, and when a key arrives some other way first.
    async awaitPairing (_context, code) {
      await ensureSession()
      const pairingRef = ref(db, pairingPath(code))
      let settle
      const promise = new Promise((resolve) => { settle = resolve })
      const listener = onValue(pairingRef, (snapshot) => {
        const record = snapshot.val()
        if (!isFreshPairing(record, Date.now(), { isValidKey })) return
        off(pairingRef, 'value', listener)
        // Fire-and-forget: a pairing left behind expires by age anyway.
        remove(pairingRef).catch(() => {})
        settle(record.key)
      }, (error) => {
        console.error('Could not listen for a pairing:', error)
      })
      return { promise, cancel: () => off(pairingRef, 'value', listener) }
    },

    // Write one line to the change log. Deliberately fire-and-forget and
    // deliberately silent on failure: a log that can break a real write is
    // worse than no log at all.
    async recordHistory ({ state }, entry) {
      if (!state.fridgeKey) return
      try {
        // Firebase rejects the whole write if any value is undefined, and a
        // missing title would take the entire line down with it.
        const record = { at: new Date().toISOString(), source: 'hand', ...entry }
        for (const field of Object.keys(record)) {
          if (record[field] === undefined) record[field] = ''
        }
        await push(ref(db, historyPath(state.fridgeKey)), record)
      } catch (error) {
        console.error('Failed to record fridge history:', error)
      }
    },

    // `source` says where the add came from and is a log field, not part of
    // the timer.
    async addTimer ({ commit, state, dispatch }, { source = 'hand', ...timer }) {
      try {
        commit('SET_LOADING', true)
        const newTimerRef = push(ref(db, timersPath(state.fridgeKey)))
        const timerData = { ...timer, createdAt: new Date().toISOString() }

        await set(newTimerRef, timerData)
        commit('SET_ERROR', null)

        const days = spanInDays(timerData.createdAt, timerData.expiryDate)
        dispatch('recordHistory', {
          action: 'added',
          title: timerData.title,
          detail: days ? formatDaySpan(days) : '',
          source
        })
      } catch (error) {
        commit('SET_ERROR', 'Failed to add timer')
        console.error('Error adding fridge timer:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Accepts a bare id or { id, source }.
    async removeTimer ({ commit, state, dispatch }, payload) {
      const id = typeof payload === 'string' ? payload : payload?.id
      const source = (typeof payload === 'object' && payload?.source) || 'hand'
      // Read the title BEFORE the delete — afterwards there is nothing left to
      // name, and "removed something" is a useless line in a change log.
      const title = state.timers[id]?.title || ''
      try {
        commit('SET_LOADING', true)
        await remove(ref(db, `${timersPath(state.fridgeKey)}/${id}`))
        commit('SET_ERROR', null)
        dispatch('recordHistory', { action: 'removed', title, source })
      } catch (error) {
        commit('SET_ERROR', 'Failed to remove timer')
        console.error('Error removing fridge timer:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // `addedDays` is what the edit actually changed, passed through so the log
    // can say "+1 week" rather than just "changed".
    async updateTimer ({ commit, state, dispatch }, { id, timer, source = 'edit', addedDays = 0 }) {
      try {
        commit('SET_LOADING', true)
        await set(ref(db, `${timersPath(state.fridgeKey)}/${id}`), {
          ...timer,
          updatedAt: new Date().toISOString()
        })
        commit('SET_ERROR', null)
        dispatch('recordHistory', {
          action: 'extended',
          title: timer.title,
          detail: addedDays > 0 ? `+${formatDaySpan(addedDays)}` : '',
          source
        })
      } catch (error) {
        commit('SET_ERROR', 'Failed to update timer')
        console.error('Error updating fridge timer:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Teach the household something about a food.
    //
    // THIS FOLDS, IT DOES NOT SET, and that is the 2026-09-20 change. It used
    // to write `days` outright — last edit wins, forever — which is how
    // sandwich bread and hamburger buns reached 76 days one extension at a
    // time. Matt: "If I modify a timer on perishable, that should inform
    // future guesses mostly. It shouldn't be seen as a fact of how long
    // something can last."
    //
    // Takes `{ title, observed, anchor }`. `observed` is one sighting, not a
    // verdict; shelfLife.js decides what it does to the running mean.
    async saveTemplate ({ state, dispatch }, { source = 'scan', title, observed, anchor, days }) {
      // `days` is still accepted for a caller that really does mean "this is
      // the number" — the hand-typed add form, where a person typed it on
      // purpose about this food rather than about one item of it.
      const observation = Number(observed ?? days)
      if (!Number.isFinite(observation) || observation <= 0) return

      const previous = state.templates[templateKey(title)]
      const next = foldObservation(previous, observation, { anchor })
      if (!next) return

      try {
        await set(ref(db, `${templatesPath(state.fridgeKey)}/${templateKey(title)}`), {
          title,
          days: next.days,
          // The precise running mean, so the belief can keep converging rather
          // than stalling on a rounding boundary. See shelfLife.js.
          mean: next.mean ?? next.days,
          count: next.count,
          anchor: next.anchor,
          updatedAt: new Date().toISOString(),
          createdAt: previous?.createdAt || new Date().toISOString()
        })
      } catch (error) {
        console.error('Failed to save fridge template:', error)
        return
      }

      // Only a CHANGE is worth a line, and now it can say how sure the house
      // is — "now 2 weeks, was 1 week (seen 4 times)" reads very differently
      // from the same sentence after one sighting.
      if (previous && Number.isInteger(previous.days) && previous.days !== next.days) {
        dispatch('recordHistory', {
          action: 'relearned',
          title,
          detail: `now ${formatDaySpan(next.days)}, was ${formatDaySpan(previous.days)} (seen ${next.count}x)`,
          source
        })
      }
    },

    async removeTemplate ({ state }, title) {
      try {
        await remove(ref(db, `${templatesPath(state.fridgeKey)}/${templateKey(title)}`))
      } catch (error) {
        console.error('Failed to remove fridge template:', error)
      }
    },

    // Apply a consumption plan that a person has already confirmed.
    //
    // Takes the plan rather than the meal on purpose. Planning is pure and
    // shown before anything happens (see consume.js); this only performs what
    // was on screen, so what you agreed to and what gets written cannot drift.
    //
    // Every change lands in the change log. A depleted timer clearing itself is
    // the one place this app removes something without being asked, so it had
    // better be able to say what happened and why.
    async applyConsumption ({ state, dispatch }, { plan, mealName = '' } = {}) {
      if (!state.fridgeKey || !plan?.uses?.length) return

      for (const use of plan.uses) {
        try {
          if (use.clears) {
            await remove(ref(db, `${timersPath(state.fridgeKey)}/${use.timerId}`))
            dispatch('recordHistory', {
              action: 'used up',
              title: use.name,
              detail: mealName ? `made ${mealName}` : '',
              source: 'meal'
            })
          } else {
            await update(ref(db, `${timersPath(state.fridgeKey)}/${use.timerId}`), {
              quantity: use.after,
              updatedAt: new Date().toISOString()
            })
            dispatch('recordHistory', {
              action: 'used some',
              title: use.name,
              detail: `${use.before} → ${use.after}${mealName ? `, made ${mealName}` : ''}`,
              source: 'meal'
            })
          }
        } catch (error) {
          // One failure must not abandon the rest. A half-applied plan is
          // recoverable — the quantities are still visible and the log says
          // what did land — whereas stopping leaves the fridge disagreeing with
          // a meal that was definitely cooked.
          console.error(`Failed to consume ${use.name}:`, error)
        }
      }
    },

    // Apply an agreed talk-through: what he said is there, and what he didn't.
    //
    // THIS IS THE ONE PLACE THAT REMOVES TIMERS IN BULK, and it does so because
    // Matt asked for exactly that — "we should assume that if I don't list it,
    // then it isn't there" (2026-09-20). Every removal was shown, checked, and
    // confirmed on the review screen before it reached here; nothing on this
    // path decides anything on its own. The log says `talk` so a removal that
    // happened because a food went unmentioned can be told apart from one
    // somebody deliberately tapped.
    //
    // The write ORDER is adds first, removals second. If the tab dies halfway
    // through, a fridge holding too much is a shopping list that is slightly
    // over-cautious; a fridge holding too little is a meal short an ingredient.
    async applyTalk ({ state, commit, dispatch }, { payload } = {}) {
      if (!state.fridgeKey || !payload) return { added: 0, removed: 0, failed: 0 }

      let added = 0
      let removed = 0
      let failed = 0

      // addTimer and removeTimer SWALLOW their own failures — they catch,
      // commit SET_ERROR and return normally — so awaiting one tells you
      // nothing about whether the write landed. Counting a completed dispatch
      // as a success would have this screen cheerfully report "added 12" after
      // twelve failures, which on the one flow that also DELETES things is the
      // worst possible lie. The store's error flag is the only signal there
      // is: clear it first, read it after.
      const didItLand = async (action, arg) => {
        commit('SET_ERROR', null)
        await dispatch(action, arg)
        return !state.error
      }

      for (const timer of payload.timers || []) {
        // One bad row must not abandon the rest — the same reasoning as
        // applyConsumption. A partial apply is visible and fixable; an
        // abandoned one leaves the fridge disagreeing with a walk that
        // definitely happened.
        if (await didItLand('addTimer', { ...timer, source: 'talk' })) added += 1
        else failed += 1
      }

      for (const template of payload.templates || []) {
        // A template is how the app gets FASTER next time, not how it is
        // correct this time. Losing one costs a re-answer, so it is not
        // counted as a failure and never holds anything up.
        await dispatch('saveTemplate', { ...template, source: 'talk' })
      }

      for (const id of payload.remove || []) {
        if (await didItLand('removeTimer', { id, source: 'talk' })) removed += 1
        else failed += 1
      }

      return { added, removed, failed }
    },

    // Bring the catalog and the fridge's templates back into agreement.
    //
    // ONLY a signed-in client may run this: it writes the grocery catalog,
    // which the wall tablet cannot even read. The wall keeps teaching templates
    // and this picks the knowledge up next time a phone opens the app.
    //
    // Writes are MERGED, never set. Writing the whole grocery-catalog node from
    // a computed object would clobber concurrent edits and anything added on
    // another device — the invariant CLAUDE.md spells out.
    async reconcileCatalog ({ state, rootState, dispatch }) {
      const hat = rootState.databaseTopKey
      if (!hat || !state.fridgeKey) return
      // No catalog yet means the subscription has not filled in, not that the
      // catalog is empty. Reconciling against nothing would publish nothing and
      // adopt everything as a new food.
      if (!Object.keys(rootState.groceryCatalog || {}).length) return

      const { toCatalog, toTemplates, newFoods, conflicts } = reconcileShelfLives({
        catalog: rootState.groceryCatalog,
        templates: state.templates
      })

      const patch = {}
      toCatalog.forEach((item) => {
        // The value AND the base move together. Recording the base is what
        // makes the next edit attributable to the side that made it.
        patch[`${item.id}/shelfLifeDays`] = item.days
        patch[`${item.id}/shelfLifeSyncedDays`] = item.days
      })
      newFoods.forEach((item) => {
        const id = fridgeFoodId(item.title)
        patch[id] = {
          id,
          name: item.title,
          shelfLifeDays: item.days,
          shelfLifeSyncedDays: item.days,
          ...(item.fridgeOnly ? { fridgeOnly: true } : {})
        }
      })

      try {
        if (Object.keys(patch).length) {
          await update(ref(db, `${hat}/grocery-catalog`), patch)
        }
        for (const item of toTemplates) {
          await dispatch('saveTemplate', { title: item.title, days: item.days, source: 'catalog' })
        }
      } catch (error) {
        // Non-fatal by design. A failed sync leaves both copies as they were;
        // it does not cost anyone a timer or a shopping-list row.
        console.error('Failed to reconcile the fridge with the catalog:', error)
        return
      }

      conflicts.forEach((c) => {
        console.warn(
          `Shelf life for ${c.name}: the fridge says ${c.fromFridge}d, the catalog said ` +
          `${c.inCatalog}d, both changed from ${c.base}d. Took the fridge's.`
        )
      })
    }
  }
}
